/* eslint-disable camelcase */
import React, { Children, Component } from 'react'

import { orgProps, productProps } from '../helpers/deal-link'
import { PageContext } from '~/contexts/page-context'
import { getCookie } from '~/utils/cookies'
import { notEmpty } from '~/utils/empty'
import { path } from '~/utils/obj'
import { omitTypename } from '~/utils/omit-typename'
import { datalayerPush } from '~/utils/tracking'

export const ECOMM_EVENT_NAME = 'eCommEvent'
export const ADALYSER_EVENT_NAME = 'adalyserEvent'
export const CLICK_EVENT_NAME = 'broadbandTableClick'

class EcommerceTracking extends Component {
  static contextType = PageContext

  constructor(props) {
    super(props)

    this.addEcommerceDataToChild = this.addEcommerceDataToChild.bind(this)
    this.trackImpressions = this.trackImpressions.bind(this)
    this.trackClick = this.trackClick.bind(this)
    this.generateImpressionId = this.generateImpressionId.bind(this)
    this.getDeals = this.getDeals.bind(this)
    this.getData = this.getData.bind(this)
    this.haveChildrenChanged = this.haveChildrenChanged.bind(this)
  }

  getComparisonId = () => {
    return this.props.show10MoreClicked ? this.state.listLoadedId : this.props.comparisonId
  }

  trackEcommerce = (data) => {
    setTimeout(() => {
      datalayerPush({
        event: ECOMM_EVENT_NAME,
        ecommerce: {
          currencyCode: 'GBP',
          ...data
        }
      })
    })
  }

  trackAdalyser = (data) =>
    setTimeout(() => {
      datalayerPush({
        ...data,
        event: ADALYSER_EVENT_NAME,
        product: 'broadband',
        type: 'adalyser',
        value: 0
      })
    })

  trackClickEvent = () =>
    setTimeout(() => {
      datalayerPush({
        event: CLICK_EVENT_NAME
      })
    })

  trackImpressions = () => {
    this.trackEcommerce({
      impressions: this.getDeals()
        .filter(({ deal }) => deal)
        .map(({ deal, list }, position) => ({
          list: list || this.props.list,
          ...this.getData(deal, position)
        }))
    })
  }

  trackClick = (list, deal, position) => () => {
    this.trackAdalyser({
      action: 'clickout',
      customerID: getCookie('uscd')
    })

    this.trackEcommerce({
      click: {
        actionField: { list: list || this.props.list },
        products: [
          {
            ...this.getData(deal, position)
          }
        ]
      }
    })

    this.trackClickEvent()
  }

  getDeals = () =>
    Children.map(this.props.children, (child) =>
      child !== null ? { list: child.props.list, deal: child.props.deal, isSponsored: !!child.props.isSponsored } : null
    ).filter((child) => child !== null)

  generateImpressionId = (id) => `${this.getComparisonId()}-${id}`

  addEcommerceDataToChild(child, idx) {
    const deal = child.props.deal
    const list = child.props.list

    return React.cloneElement(child, {
      ecommerce: { trackClick: this.trackClick(list, deal, idx) }
    })
  }

  haveChildrenChanged = (prevProps, nextProps) => {
    const prevDeals = React.Children.map(prevProps.children, ({ props }) => props.deal)
    const nextDeals = React.Children.map(nextProps.children, ({ props }) => props.deal)

    return JSON.stringify(prevDeals, omitTypename) !== JSON.stringify(nextDeals, omitTypename)
  }

  // @TODO: this should be handled better when we refactor away from classes
  shouldComponentUpdate(nextProps, nextState) {
    const didPostCodeChange = this.props.postcode !== nextProps.postcode
    const didAddressChange =
      (this.props.address || {}).uniquedeliverypointreferencenumber !==
      (nextProps.address || {}).uniquedeliverypointreferencenumber
    const didChildrenDealsChange = this.haveChildrenChanged(this.props, nextProps)
    const hasUspAnimatedChange = this.props.hasUspAnimated !== nextProps.hasUspAnimated

    return didPostCodeChange || didAddressChange || didChildrenDealsChange || hasUspAnimatedChange
  }

  getData = (deal, position) => ({
    position: position + 1,
    name: deal.title,
    id: deal.productId.toString(),
    brand: deal.provider.name,
    variant: deal.id,
    dimension86: deal.id,
    dimension87: this.generateImpressionId(deal.id),
    dimension91: this.getComparisonId(),
    ...this.props.metadata
  })

  componentDidMount() {
    this.trackImpressions()
    this.setState({
      listLoadedId: this.getComparisonId()
    })
  }

  componentDidUpdate(prevProps, prevState) {
    // If user filters or sorts then set listLoadedId to this comparison Id
    if (!this.props.show10MoreClicked) {
      this.setState({
        listLoadedId: this.getComparisonId()
      })
    }

    if (this.haveChildrenChanged(prevProps, this.props)) {
      this.trackImpressions()
    }
  }

  productListProps() {
    const { pageId } = this.context
    const { postcode, address } = this.props

    const comparisonId = this.getComparisonId()
    const staticFormId = pageId === 'broadband' ? 'fixed_form' : 'availability_checker_form'
    const serviceableCount = this.getDeals().filter((deal) =>
      path(['deal', 'services', 'broadband', 'realSpeeds'], deal)
    ).length
    const serviceabilityStatus = [
      {
        address: notEmpty(address),
        postcode: notEmpty(postcode),
        products_shown: Boolean(serviceableCount)
      }
    ]

    const productsPerPage = 10

    const dealsCount = this.getDeals().length

    const totalNumOfDeals = this.props.dealsCount + this.props.tenanciesCount

    const differenceBetweenDealsAndTotalCount = dealsCount - totalNumOfDeals

    const remainingDeals = this.props.comparison.length % productsPerPage

    const sliceCount = () => {
      if (this.props.comparison.length <= productsPerPage && this.props.tenanciesCount > 0) {
        return -dealsCount
      }
      if (differenceBetweenDealsAndTotalCount <= -productsPerPage) {
        return -productsPerPage
      }
      return remainingDeals === 0 ? -productsPerPage : -remainingDeals
    }

    const products = this.getDeals()
      .reduce((acc, { deal, isSponsored }, idx) => {
        if (!deal) {
          return acc
        }

        return [
          ...acc,
          productProps(pageId, { ...deal, isSponsored }, comparisonId, idx + 1, deal.clickoutLinks[0].url)
        ]
      }, [])
      .slice(sliceCount())

    return {
      product_list_results_page_type: this.getDeals()
        .slice(sliceCount())
        .some((deal) => deal.deal.personalisedFeatures)
        ? 'ANNUAL_SAVINGS'
        : 'DEFAULT',
      form_id: this.context.formId || staticFormId,
      list_id: pageId,
      list_loaded_id: this.props.sort === 'HERO' ? `${comparisonId}-p0` : comparisonId,
      product_list_name: pageId,
      products,
      ...((address || postcode) && { serviceability_status: serviceabilityStatus }),
      ...orgProps
    }
  }

  render() {
    const children = React.Children.map(this.props.children, (child, index) =>
      child?.props?.deal ? this.addEcommerceDataToChild(child, index) : child
    )

    return (
      <div
        data-nerd
        data-nerd-type='product_list'
        data-nerd-props={JSON.stringify(this.productListProps())}
        data-nerd-observable
      >
        {children}
      </div>
    )
  }
}

export default EcommerceTracking
